export type ResolvedVideo = {
  kind: 'iframe' | 'file' | 'empty';
  src: string;
};

export interface VideoEmbedOptions {
  autoplay?: boolean;
  loop?: boolean;
  controls?: boolean;
}

const YOUTUBE_ID = /^[a-zA-Z0-9_-]{11}$/;

function parseUrl(raw: string): URL | null {
  try {
    return new URL(raw.startsWith('http://') || raw.startsWith('https://') ? raw : `https://${raw}`);
  } catch {
    return null;
  }
}

function youtubeIdFromUrl(url: URL): string | null {
  const host = url.hostname.replace(/^www\./i, '').toLowerCase();
  const parts = url.pathname.split('/').filter(Boolean);
  let id = '';

  if (host === 'youtu.be') {
    id = parts[0] || '';
  } else if (host === 'youtube.com' || host === 'm.youtube.com' || host === 'music.youtube.com' || host === 'youtube-nocookie.com') {
    if (parts[0] === 'embed' || parts[0] === 'shorts' || parts[0] === 'live' || parts[0] === 'v') {
      id = parts[1] || '';
    } else {
      id = url.searchParams.get('v') || '';
    }
  }

  const cleaned = id.replace(/[^a-zA-Z0-9_-]/g, '').slice(0, 11);
  return YOUTUBE_ID.test(cleaned) ? cleaned : null;
}

function parseStartSeconds(url: URL): number | null {
  const raw = url.searchParams.get('start') || url.searchParams.get('t') || '';
  if (!raw) return null;
  const asNumber = Number.parseInt(raw, 10);
  if (Number.isFinite(asNumber) && asNumber > 0) return asNumber;
  const match = raw.match(/(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?/i);
  if (!match) return null;
  const hours = Number(match[1] || 0);
  const minutes = Number(match[2] || 0);
  const seconds = Number(match[3] || 0);
  const total = hours * 3600 + minutes * 60 + seconds;
  return total > 0 ? total : null;
}

/** Convert a pasted YouTube / Shorts / youtu.be / Vimeo / file URL into a playable src. */
export function resolveVideoSrc(raw: string, options: VideoEmbedOptions = {}): ResolvedVideo {
  const input = String(raw || '').trim();
  if (!input) return { kind: 'empty', src: '' };

  const url = parseUrl(input);
  if (!url) return { kind: 'file', src: input };

  const youtubeId = youtubeIdFromUrl(url);
  if (youtubeId) {
    const params = new URLSearchParams();
    if (options.autoplay) {
      params.set('autoplay', '1');
      params.set('mute', '1');
    }
    if (options.loop) {
      params.set('loop', '1');
      params.set('playlist', youtubeId);
    }
    if (options.controls === false) params.set('controls', '0');
    const start = parseStartSeconds(url);
    if (start) params.set('start', String(start));
    const query = params.toString();
    return {
      kind: 'iframe',
      src: `https://www.youtube.com/embed/${youtubeId}${query ? `?${query}` : ''}`,
    };
  }

  const host = url.hostname.replace(/^www\./i, '').toLowerCase();
  if (host === 'vimeo.com' || host === 'player.vimeo.com') {
    const id = url.pathname.split('/').filter(Boolean).pop() || '';
    if (/^\d+$/.test(id)) {
      return { kind: 'iframe', src: `https://player.vimeo.com/video/${id}` };
    }
  }

  if (host === 'youtu.be' || host.endsWith('youtube.com') || host === 'youtube-nocookie.com') {
    return { kind: 'empty', src: '' };
  }

  return { kind: 'file', src: input };
}
